<script setup lang="ts">
import Button from 'primevue/button'
import InputText from 'primevue/inputtext'
import { Icon } from '@iconify/vue'
import { computed, onMounted, ref } from 'vue'
import { getUser } from '@/api/users'
import type { User } from '@/types/user-types'
import { UserRole } from '@/constants/user'
import ProgressSpinner from 'primevue/progressspinner'
import { useToast } from 'primevue/usetoast'
import { getUserId } from '@/constants'
import defaultAvatar from '@/assets/images/user_avatar.png'

const toast = useToast();
const userId = getUserId();

const user = ref<User | null>(null);
const isLoadingUser = ref(false);
const errorMessage = ref<string | null>(null);
const password = ref('');
const confirmPassword = ref('');

const fetchCurrentUser = async () => {
  if (!userId) {
    errorMessage.value = "User ID not found. Cannot load profile.";
    return;
  }
  isLoadingUser.value = true;
  errorMessage.value = null;
  try {
    user.value = await getUser(userId);
  } catch (error: any) {
    console.error("Failed to fetch user profile:", error);
    errorMessage.value = error.response?.data?.message || error.message || "Could not load user profile.";
    toast.add({ severity: 'error', summary: 'Error', detail: errorMessage.value, life: 3000 });
  } finally {
    isLoadingUser.value = false;
  }
};

onMounted(() => {
  fetchCurrentUser();
});

const userFullName = computed(() => {
  if (!user.value) return 'Loading...';
  return `${user.value.firstName} ${user.value.lastName}`;
});

const userRoleDisplay = computed(() => {
  if (!user.value) return 'Loading...';
  switch (user.value.userRole) {
    case UserRole.Admin:
      return 'Admin';
    case UserRole.User:
      return 'User';
    default:
      return 'Unknown Role';
  }
});

const handleEditProfile = () => {
  // TODO: Navigate to an edit profile page or open a modal
  toast.add({ severity: 'info', summary: 'Info', detail: 'Edit Profile functionality not yet implemented.', life: 3000 });
};

const handleChangePassword = () => {
  if (password.value !== confirmPassword.value) {
    toast.add({ severity: 'error', summary: 'Error', detail: 'Passwords do not match.', life: 3000 });
    return;
  }
  if (!password.value) {
    toast.add({ severity: 'warn', summary: 'Validation', detail: 'Password cannot be empty.', life: 3000 });
    return;
  }
  console.log('Change Password clicked. Password:', password.value);
  // TODO: Implement password change logic (call API)
  toast.add({ severity: 'info', summary: 'Info', detail: 'Change Password functionality not yet implemented.', life: 3000 });
  password.value = '';
  confirmPassword.value = '';
};
</script>

<template>
    <div class="p-6 md:p-8 lg:p-10 bg-gray-50 min-h-screen">
      <h1 class="text-2xl md:text-3xl font-semibold text-gray-800 mb-8">User Profile</h1>

      <div v-if="isLoadingUser" class="flex justify-center items-center py-10">
        <ProgressSpinner strokeWidth="3" style="width: 50px; height: 50px" />
      </div>

      <div v-if="errorMessage && !isLoadingUser" class="p-4 mb-4 text-sm text-red-700 bg-red-100 rounded-lg" role="alert">
        {{ errorMessage }}
      </div>

      <template v-if="user && !isLoadingUser">
        <!-- User Information Card -->
        <div class="bg-white p-6 md:p-8 rounded-xl shadow-lg mb-8">
          <div class="flex flex-col sm:flex-row items-center gap-6">
            <img
              :src="defaultAvatar"
            alt="User Avatar"
            class="w-20 h-20 md:w-24 md:h-24 rounded-full object-cover border-2 border-gray-200"
            />
            <div class="flex-grow text-center sm:text-left">
              <h2 class="text-xl md:text-2xl font-semibold text-gray-800">{{ userFullName }}</h2>
              <p class="text-md text-gray-600">{{ userRoleDisplay }}</p>
              <p class="text-sm text-gray-500">{{ user.email }}</p>
            </div>
            <Button
              label="Edit Profile"
              icon="pi pi-pencil"
              class="p-button-primary w-full sm:w-auto"
            @click="handleEditProfile"
            />
          </div>
        </div>

        <!-- Password Change Card -->
        <div class="bg-white p-6 md:p-8 rounded-xl shadow-lg">
          <div class="flex flex-col sm:flex-row justify-between items-center mb-6 pb-4 border-b border-gray-200">
            <h3 class="text-lg md:text-xl font-semibold text-gray-700 mb-3 sm:mb-0">Password Change</h3>
            <Button
              label="Change Password"
              class="p-button-primary w-full sm:w-auto"
            @click="handleChangePassword"
            :disabled="!password || password !== confirmPassword"
            />
          </div>

          <div class="space-y-6">
            <div>
              <label for="password" class="block text-sm font-medium text-gray-700 mb-1">New Password:</label>
              <div class="relative">
                <InputText
                  id="password"
                  type="password"
                  v-model="password"
                  placeholder="Enter your new password"
                  class="w-full"
                  :inputClass="'py-3'"
                />
                <span class="absolute inset-y-0 right-0 pr-3 flex items-center cursor-pointer text-gray-400">
                  <Icon icon="mdi:eye-off-outline" class="text-xl" />
                </span>
              </div>
            </div>

            <div>
              <label for="confirmPassword" class="block text-sm font-medium text-gray-700 mb-1">Confirm New Password:</label>
              <div class="relative">
                <InputText
                  id="confirmPassword"
                  type="password"
                  v-model="confirmPassword"
                  placeholder="Re-enter new password"
                  class="w-full"
                  :inputClass="'py-3'"
                />
                <span class="absolute inset-y-0 right-0 pr-3 flex items-center cursor-pointer text-gray-400">
                  <Icon icon="mdi:eye-off-outline" class="text-xl" />
                </span>
              </div>
              <p v-if="password && confirmPassword && password !== confirmPassword" class="text-red-500 text-xs mt-1">
                Passwords do not match.
              </p>
            </div>
          </div>
        </div>
      </template>
    </div>
</template>

<style scoped>
:deep(.p-inputtext) {
  padding-top: 0.75rem !important;
  padding-bottom: 0.75rem !important;
}
</style>